/**
 * DocPreview — inline preview of a source document (invoice scan / PDF).
 *
 * Takes EITHER a local File (just-uploaded, previewed before anything is
 * posted) OR a server `filePath` ("uploads/…"), which is fetched as an
 * authorized blob so the iframe never hits cross-origin or auth walls.
 * Used on the entry review step and in the Records drawer, so the person
 * confirming a number always sees the document it came from.
 */
import React, { useEffect, useState } from 'react';
import { useT } from '../../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export function DocPreview({ file, filePath, height = 360 }) {
  const { t } = useT();
  const [url, setUrl] = useState(null);
  const [kind, setKind] = useState('pdf');
  const [state, setState] = useState('loading'); // loading | ready | gone | error

  useEffect(() => {
    let objectUrl = null;
    let aborted = false;
    setUrl(null);
    setState('loading');

    if (file) {
      objectUrl = URL.createObjectURL(file);
      setKind(file.type && file.type.startsWith('image/') ? 'image' : 'pdf');
      setUrl(objectUrl);
      setState('ready');
    } else if (filePath) {
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_BASE}/${filePath}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (aborted) return;
          if (res.status === 404) { setState('gone'); return; }
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          if (aborted) return;
          objectUrl = URL.createObjectURL(blob);
          setKind(blob.type.startsWith('image/') ? 'image' : 'pdf');
          setUrl(objectUrl);
          setState('ready');
        } catch (e) {
          if (!aborted) setState('error');
        }
      })();
    } else {
      setState('gone');
    }

    return () => {
      aborted = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, filePath]);

  const frame = {
    width: '100%', height, border: '1px solid var(--n200, #e3e6e4)',
    borderRadius: 10, background: 'var(--n50, #fafbfa)', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  if (state === 'loading') {
    return <div style={frame}><span style={{ fontSize: 12.5, color: 'var(--n500)' }}>{t('docprev.loading')}</span></div>;
  }
  if (state === 'gone') {
    return <div style={frame}><span style={{ fontSize: 12.5, color: 'var(--n500)' }}>{t('invdet.fileGone')}</span></div>;
  }
  if (state === 'error') {
    return <div style={frame}><span style={{ fontSize: 12.5, color: 'var(--n500)' }}>{t('invdet.previewFailed')}</span></div>;
  }
  return (
    <div style={frame}>
      {kind === 'image' ? (
        <img src={url} alt={t('docprev.alt')} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      ) : (
        <iframe src={`${url}#toolbar=0&navpanes=0`} title={t('docprev.alt')} style={{ width: '100%', height: '100%', border: 'none' }} />
      )}
    </div>
  );
}

export default DocPreview;
